import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { MessageCircle, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABEL = {
  pending_admin_approval: { text: 'Awaiting admin approval', color: 'bg-yellow-100 text-yellow-800' },
  admin_rejected: { text: 'Rejected by admin', color: 'bg-red-100 text-red-800' },
  approved_awaiting_carrier: { text: 'Sent to carrier', color: 'bg-blue-100 text-blue-800' },
  carrier_reply_pending_admin: { text: 'Carrier replied — awaiting admin', color: 'bg-yellow-100 text-yellow-800' },
  carrier_reply_admin_rejected: { text: 'Carrier reply rejected', color: 'bg-red-100 text-red-800' },
  completed: { text: 'Completed', color: 'bg-green-100 text-green-800' },
};

// Renders the negotiation thread for a specific quote from the shipper's perspective.
// Also exposes an inline "Start Negotiation" form when no open negotiation exists.
export const ShipperQuoteNegotiation = ({ quoteId, shipperEmail, highlightNegotiationId }) => {
  const [negotiations, setNegotiations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fetchNegotiations = async () => {
    try {
      const res = await axios.get(`${API}/quotes/${quoteId}/negotiations?viewer_email=${encodeURIComponent(shipperEmail)}`);
      setNegotiations(res.data.negotiations || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNegotiations(); }, [quoteId, shipperEmail]);

  // Scroll to & flash target negotiation card when deep-linked
  useEffect(() => {
    if (!highlightNegotiationId || negotiations.length === 0) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`negotiation-${highlightNegotiationId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('qa-highlight-flash');
        setTimeout(() => el.classList.remove('qa-highlight-flash'), 4000);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [highlightNegotiationId, negotiations]);

  const hasOpenNegotiation = negotiations.some(n => [
    'pending_admin_approval',
    'approved_awaiting_carrier',
    'carrier_reply_pending_admin',
  ].includes(n.status));

  const handleSubmit = async () => {
    setError('');
    if (message.trim().length < 10) {
      setError('Please provide at least 10 characters explaining your negotiation.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes/${quoteId}/negotiate`, {
        shipper_email: shipperEmail,
        shipper_message: message.trim(),
      });
      setMessage('');
      setShowForm(false);
      await fetchNegotiations();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to submit negotiation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-dashed">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#1e3a5f]">
          <MessageCircle className="w-4 h-4" />
          Negotiations
        </div>
        {!showForm && !hasOpenNegotiation && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowForm(true)}
            className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white"
            data-testid={`negotiate-btn-${quoteId}`}
          >
            <MessageCircle className="w-3.5 h-3.5 mr-1" />
            Negotiate
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="p-3 mb-3 border-blue-200 bg-blue-50">
          <Label className="text-xs">Explain what you&apos;d like to negotiate</Label>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="e.g. Would you consider dropping the price if I flex the pickup date by 2 days?"
            rows={3}
            className="text-sm mb-2"
            data-testid={`negotiate-message-${quoteId}`}
          />
          {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#1e3a5f] hover:bg-[#15304f] text-white"
              data-testid={`negotiate-submit-${quoteId}`}
            >
              <Send className="w-3.5 h-3.5 mr-1" />
              {submitting ? 'Sending...' : 'Send to Admin for Review'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setShowForm(false); setError(''); }}>Cancel</Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">Your message will be reviewed by admin before the carrier can see it.</p>
        </Card>
      )}

      {loading ? (
        <p className="text-xs text-gray-500">Loading...</p>
      ) : negotiations.length === 0 ? (
        <p className="text-xs text-gray-500">No negotiations yet.</p>
      ) : (
        <div className="space-y-3">
          {negotiations.map(n => {
            const label = STATUS_LABEL[n.status] || { text: n.status, color: 'bg-gray-100 text-gray-800' };
            return (
              <Card
                key={n.id}
                id={`negotiation-${n.id}`}
                data-testid={`negotiation-card-${n.id}`}
                className="p-3 border-l-4 border-l-blue-400 transition-all duration-500"
              >
                <div className="flex justify-between items-start mb-2">
                  <p className="text-xs text-gray-500">{new Date(n.created_at).toLocaleString()}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${label.color}`}>{label.text}</span>
                </div>
                <p className="text-sm text-gray-700 mb-2"><strong>You:</strong> {n.shipper_message}</p>
                {n.status === 'admin_rejected' && n.admin_rejection_reason && (
                  <p className="text-xs text-red-700 bg-red-50 p-2 rounded">Admin: {n.admin_rejection_reason}</p>
                )}
                {n.carrier_reply_message && n.status === 'completed' && (
                  <div className={`p-2 rounded text-sm ${n.carrier_decision === 'accepted' ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`}>
                    <p className="font-semibold mb-1">
                      Carrier {n.carrier_decision === 'accepted' ? 'accepted' : 'declined'}:
                    </p>
                    <p>{n.carrier_reply_message}</p>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Renders a carrier's list of pending negotiations with Accept/Decline forms.
export const CarrierNegotiationsPanel = ({ carrierEmail, highlightNegotiationId }) => {
  const [negotiations, setNegotiations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyForms, setReplyForms] = useState({}); // { negId: { decision, message, submitting, error } }

  const fetchNegotiations = async () => {
    if (!carrierEmail) return;
    try {
      const res = await axios.get(`${API}/negotiations/carrier/${encodeURIComponent(carrierEmail)}`);
      setNegotiations(res.data.negotiations || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNegotiations(); }, [carrierEmail]);

  useEffect(() => {
    if (!highlightNegotiationId || negotiations.length === 0) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`negotiation-${highlightNegotiationId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('qa-highlight-flash');
        setTimeout(() => el.classList.remove('qa-highlight-flash'), 4000);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [highlightNegotiationId, negotiations]);

  const setForm = (id, patch) => setReplyForms(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));

  const handleSubmit = async (negotiation, decision) => {
    const form = replyForms[negotiation.id] || {};
    const message = (form.message || '').trim();
    if (message.length < 10) {
      setForm(negotiation.id, { error: 'Reply must be at least 10 characters' });
      return;
    }
    setForm(negotiation.id, { submitting: true, error: '' });
    try {
      await axios.post(`${API}/negotiations/${negotiation.id}/carrier-respond`, {
        carrier_email: carrierEmail,
        decision,
        reply_message: message,
      });
      await fetchNegotiations();
      setForm(negotiation.id, { message: '', submitting: false });
    } catch (e) {
      setForm(negotiation.id, { error: e.response?.data?.detail || 'Failed to submit reply', submitting: false });
    }
  };

  if (loading) return null;
  if (negotiations.length === 0) return null;

  return (
    <Card className="p-4 mb-4 border-l-4 border-l-blue-500" data-testid="carrier-negotiations-panel">
      <h3 className="text-lg font-bold text-[#1e3a5f] mb-3 flex items-center gap-2">
        <MessageCircle className="w-5 h-5" />
        Shipper Negotiations ({negotiations.length})
      </h3>
      <div className="space-y-3">
        {negotiations.map(n => {
          const label = STATUS_LABEL[n.status] || { text: n.status, color: 'bg-gray-100 text-gray-800' };
          const canRespond = n.status === 'approved_awaiting_carrier';
          const form = replyForms[n.id] || {};
          return (
            <Card
              key={n.id}
              id={`negotiation-${n.id}`}
              data-testid={`carrier-negotiation-${n.id}`}
              className="p-3 bg-white transition-all duration-500"
            >
              <div className="flex justify-between items-start mb-2">
                <p className="text-xs text-gray-500">{new Date(n.created_at).toLocaleString()}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${label.color}`}>{label.text}</span>
              </div>
              <p className="text-sm text-gray-700 mb-3">
                <strong>Shipper ({n.shipper_email}):</strong> {n.shipper_message}
              </p>

              {canRespond && (
                <div className="mt-2 border-t pt-3">
                  <Label className="text-xs">Your reply (min 10 characters)</Label>
                  <Textarea
                    value={form.message || ''}
                    onChange={(e) => setForm(n.id, { message: e.target.value })}
                    placeholder="e.g. Happy to reduce by $200 if pickup is Monday."
                    rows={3}
                    className="text-sm mb-2"
                    data-testid={`neg-reply-input-${n.id}`}
                  />
                  {form.error && <p className="text-xs text-red-600 mb-2">{form.error}</p>}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleSubmit(n, 'accepted')}
                      disabled={form.submitting}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`neg-accept-${n.id}`}
                    >
                      Accept &amp; Reply
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleSubmit(n, 'declined')}
                      disabled={form.submitting}
                      variant="outline"
                      className="border-red-600 text-red-600 hover:bg-red-50"
                      data-testid={`neg-decline-${n.id}`}
                    >
                      Decline &amp; Reply
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Your reply is reviewed by admin before the shipper sees it.</p>
                </div>
              )}

              {n.carrier_reply_message && n.status !== 'approved_awaiting_carrier' && (
                <div className={`p-2 rounded text-sm mt-2 ${n.carrier_decision === 'accepted' ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`}>
                  <p className="font-semibold mb-1">Your reply ({n.carrier_decision}):</p>
                  <p>{n.carrier_reply_message}</p>
                </div>
              )}
              {n.status === 'carrier_reply_admin_rejected' && n.admin_reply_rejection_reason && (
                <p className="text-xs text-red-700 bg-red-50 p-2 rounded mt-2">Admin: {n.admin_reply_rejection_reason}</p>
              )}
            </Card>
          );
        })}
      </div>
    </Card>
  );
};

export default ShipperQuoteNegotiation;
